import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activateWeightVector,
  createWeightVector,
  listFactors,
  listStrategies,
  listWeightVectors,
  type FactorDescriptor,
  type StrategyTemplateOut,
  type WeightVectorOut,
} from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput, Select, TextInput } from "@/components/ui/Input";
import { Pill } from "@/components/ui/Pill";
import { EmptyState, LoadingState } from "@/components/ui/States";

// Curated factor-family display order. Higher = lower (i.e. appears later).
// Fundamentals first because they're the most familiar; sentiment / attention
// last because they're newer and operators may not weight them by default.
const _FAMILY_ORDER: Record<string, number> = {
  valuation: 10,
  quality: 20,
  growth: 30,
  momentum: 40,
  revisions: 50,
  balance_sheet: 60,
  cash_flow: 70,
  capital_efficiency: 80,
  volatility: 90,
  liquidity: 100,
  technical: 110,
  etf: 120,
  crypto: 130,
  sentiment: 140,
  attention: 150,
};

function familyOrder(family: string): number {
  return _FAMILY_ORDER[family] ?? 999;
}

// Display title for a family — capitalised and underscore-stripped.
function familyTitle(family: string): string {
  const labels: Record<string, string> = {
    valuation: "Valuation",
    quality: "Quality",
    growth: "Growth",
    momentum: "Momentum",
    revisions: "Revisions",
    balance_sheet: "Balance sheet",
    cash_flow: "Cash flow",
    capital_efficiency: "Capital efficiency",
    volatility: "Volatility",
    liquidity: "Liquidity",
    technical: "Technical",
    etf: "ETF",
    crypto: "Crypto",
    sentiment: "Sentiment",
    attention: "Attention",
  };
  return labels[family] ?? family.replace(/_/g, " ");
}

/**
 * Settings page — weight-vector authoring.
 *
 * Lists every saved weight vector (per-name versions, active flagged),
 * exposes a creator that starts from a strategy template or a blank
 * sheet, and lets the operator pin a version active for its name.
 *
 * The backend versions vectors per name on save: re-saving with the
 * same name auto-bumps to ``max(version)+1`` so historical scores
 * remain interpretable. The page surfaces that with a "v.N" pill in the
 * table.
 */
export function SettingsPage() {
  const factors = useQuery({
    queryKey: ["factors"],
    queryFn: listFactors,
  });
  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: listStrategies,
  });
  const vectors = useQuery({
    queryKey: ["weight-vectors"],
    queryFn: () => listWeightVectors(),
  });

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="text-2xs text-foreground-muted">
          Author weight vectors for the scoring engine. Editing creates a new
          version; activating swaps the version used by every page that hits
          the active scorer.
        </p>
      </header>

      {(factors.isLoading || strategies.isLoading || vectors.isLoading) && (
        <LoadingState />
      )}

      {factors.data && strategies.data && (
        <WeightVectorEditor
          factors={factors.data}
          strategies={strategies.data}
        />
      )}

      <Panel
        title="Saved weight vectors"
        description="Every version is preserved. Click activate to make a version live."
        bodyClassName="px-0 py-0"
      >
        <SavedVectorsTable vectors={vectors.data ?? []} />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

function WeightVectorEditor({
  factors,
  strategies,
}: {
  factors: FactorDescriptor[];
  strategies: StrategyTemplateOut[];
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("default");
  const [description, setDescription] = useState("");
  const [template, setTemplate] = useState<string>("custom");
  const [assetClass, setAssetClass] = useState<string>("equity");
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [activate, setActivate] = useState<boolean>(true);

  // Pre-populate weights from the selected template (excluding "custom"
  // which leaves the editor untouched).
  useEffect(() => {
    if (template === "custom") return;
    const tpl = strategies.find((s) => s.template === template);
    if (tpl) {
      setWeights({ ...tpl.weights });
    }
  }, [template, strategies]);

  const factorByFamily = useMemo(() => {
    const groups = new Map<string, FactorDescriptor[]>();
    for (const f of factors) {
      if (!groups.has(f.family)) groups.set(f.family, []);
      groups.get(f.family)!.push(f);
    }
    return Array.from(groups.entries()).sort(
      ([a], [b]) => familyOrder(a) - familyOrder(b) || a.localeCompare(b),
    );
  }, [factors]);

  const totalWeight = useMemo(
    () => Object.values(weights).reduce((s, v) => s + (Number(v) || 0), 0),
    [weights],
  );

  // Family-level totals: the scoring engine renormalises weights *within* each
  // family at composite time (see ``services/scoring/composite.py``) so each
  // family's sub-score is independent of how heavily that family is weighted
  // in the composite.  Surface that so the operator sees what the runner will
  // actually do — a single factor inside a family is effectively 100% of that
  // family's signal regardless of its raw weight.
  const familyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const f of factors) {
      const w = Number(weights[f.name]) || 0;
      if (w > 0) {
        totals[f.family] = (totals[f.family] ?? 0) + w;
      }
    }
    return totals;
  }, [factors, weights]);

  const create = useMutation({
    mutationFn: () =>
      createWeightVector({
        name: name.trim(),
        description: description.trim() || null,
        weights,
        strategy_template: template,
        applicable_security_type:
          assetClass === "any" ? null : assetClass,
        activate,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weight-vectors"] });
    },
  });

  const canSubmit =
    name.trim().length > 0 &&
    Object.keys(weights).length > 0 &&
    totalWeight > 0 &&
    Object.values(weights).every(
      (v) => Number.isFinite(v) && (v as number) >= 0,
    );

  return (
    <Panel
      title="Author / save weight vector"
      description="Pick a template or build from scratch. Saving creates a new version."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="wv-name">Name</Label>
            <TextInput
              id="wv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="default"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wv-asset-class">Asset class</Label>
            <Select
              id="wv-asset-class"
              value={assetClass}
              onChange={(e) => setAssetClass(e.target.value)}
            >
              <option value="equity">equity</option>
              <option value="etf">etf</option>
              <option value="crypto">crypto</option>
              <option value="adr">adr</option>
              <option value="gdr">gdr</option>
              <option value="reit">reit</option>
              <option value="preferred">preferred</option>
              <option value="other">other</option>
              <option value="any">any (no scope)</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wv-template">Strategy template</Label>
            <Select
              id="wv-template"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            >
              <option value="custom">Custom (start blank)</option>
              {strategies.map((s) => (
                <option key={s.template} value={s.template}>
                  {s.template}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wv-description">Description</Label>
            <TextInput
              id="wv-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="space-y-3">
          {factorByFamily.map(([family, fs]) => (
            <FamilyGroup
              key={family}
              family={family}
              factors={fs}
              weights={weights}
              totalWeight={totalWeight}
              familyTotal={familyTotals[family] ?? 0}
              onWeightChange={(name, v) =>
                setWeights((prev) => {
                  const next = { ...prev };
                  if (v === null) delete next[name];
                  else next[name] = v;
                  return next;
                })
              }
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <div className="text-xs text-foreground-muted">
            <span>Total weight: </span>
            <span
              className={`tabular-nums font-medium ${
                totalWeight > 0 && Math.abs(totalWeight - 1) < 0.0005
                  ? "text-success"
                  : totalWeight > 0
                    ? "text-warning"
                    : "text-foreground-primary"
              }`}
            >
              {totalWeight.toFixed(3)}
            </span>
            <span className="ml-2">
              ({Object.keys(weights).length} factors)
            </span>
            {totalWeight > 0 && Math.abs(totalWeight - 1) >= 0.0005 && (
              <span className="ml-2 text-2xs text-foreground-muted">
                — engine will renormalise to 1.0
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            Activate immediately for this name
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit || create.isPending}
            loading={create.isPending}
          >
            Save new version
          </Button>
        </div>

        {create.isError && (
          <div className="text-xs text-danger">
            {(create.error as Error).message}
          </div>
        )}
        {create.isSuccess && (
          <div className="text-xs text-foreground-muted">
            Saved {create.data.name} v{create.data.version}
            {create.data.is_active ? " (active)" : ""}.
          </div>
        )}
      </form>
    </Panel>
  );
}

function FamilyGroup({
  family,
  factors,
  weights,
  totalWeight,
  familyTotal,
  onWeightChange,
}: {
  family: string;
  factors: FactorDescriptor[];
  weights: Record<string, number>;
  totalWeight: number;
  familyTotal: number;
  onWeightChange: (name: string, value: number | null) => void;
}) {
  // Family share of the composite — how much of the composite signal this
  // family carries after the engine renormalises composite weights to 1.0.
  const familyShareOfComposite =
    totalWeight > 0 ? (familyTotal / totalWeight) * 100 : 0;
  const activeCount = factors.filter((f) => (weights[f.name] ?? 0) > 0).length;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-2xs uppercase tracking-wider text-foreground-muted">
          {familyTitle(family)}
        </h3>
        {familyTotal > 0 && (
          <div className="flex items-center gap-2 text-2xs text-foreground-muted">
            <span
              className="tabular-nums"
              title="Share of the composite this family will carry after the engine renormalises composite weights to 1.0"
            >
              {familyShareOfComposite.toFixed(1)}% of composite
            </span>
            <span aria-hidden="true">·</span>
            <span title="Number of active factors in this family">
              {activeCount} active
            </span>
          </div>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {factors.map((f) => {
          const v = weights[f.name];
          const overallPct =
            v !== undefined && v > 0 && totalWeight > 0
              ? ((v / totalWeight) * 100).toFixed(1)
              : null;
          // Within-family share: the engine renormalises weights *within* each
          // family for the per-family sub-score. A single active factor in a
          // family ⇒ that factor is 100% of the family's sub-score.
          const familyPct =
            v !== undefined && v > 0 && familyTotal > 0
              ? ((v / familyTotal) * 100).toFixed(1)
              : null;
          return (
            <div
              key={f.name}
              className="flex items-center gap-2 rounded-md border border-border bg-surface-base px-2 py-1.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs text-foreground-primary">
                    {f.name}
                  </span>
                  <Pill tone={f.direction === 1 ? "muted" : "warn"}>
                    {f.direction === 1 ? "↑ better" : "↓ better"}
                  </Pill>
                </div>
                <div className="truncate text-2xs text-foreground-muted">
                  {f.description}
                </div>
              </div>
              <NumberInput
                aria-label={`weight for ${f.name}`}
                value={v ?? ""}
                onChange={(e) => {
                  const text = e.target.value;
                  if (text === "") {
                    onWeightChange(f.name, null);
                    return;
                  }
                  const num = Number(text);
                  if (!Number.isFinite(num) || num < 0) return;
                  onWeightChange(f.name, num);
                }}
                step="0.01"
                min={0}
                className="w-20 text-right"
              />
              <div
                className="flex w-20 shrink-0 flex-col items-end text-2xs tabular-nums leading-tight"
                title={
                  familyPct !== null
                    ? `Family-renormalised: ${familyPct}% of ${family.replace(/_/g, " ")} sub-score · Composite: ${overallPct}% of composite`
                    : "Set a weight to preview normalised share"
                }
              >
                <span className="text-foreground-secondary">
                  {familyPct !== null ? `${familyPct}% fam` : ""}
                </span>
                <span className="text-foreground-muted">
                  {overallPct !== null ? `${overallPct}% all` : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved vectors table
// ---------------------------------------------------------------------------

function SavedVectorsTable({ vectors }: { vectors: WeightVectorOut[] }) {
  const qc = useQueryClient();
  const activate = useMutation({
    mutationFn: (id: number) => activateWeightVector(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weight-vectors"] });
    },
  });

  if (vectors.length === 0) {
    return (
      <EmptyState
        title="No saved weight vectors yet"
        description="Save one above to get started."
      />
    );
  }

  return (
    <DataTable
      dense
      rows={vectors}
      rowKey={(r) => r.id}
      empty="No vectors."
      columns={[
        {
          key: "name",
          header: "Name",
          cell: (r) => (
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground-primary">
                {r.name}
              </span>
              <Pill tone="muted">v{r.version}</Pill>
              {r.is_active && <Pill tone="ok">active</Pill>}
            </div>
          ),
        },
        {
          key: "asset_class",
          header: "Asset class",
          cell: (r) => (
            <Pill tone="muted">
              {r.applicable_security_type ?? "any"}
            </Pill>
          ),
        },
        {
          key: "template",
          header: "Template",
          cell: (r) => (
            <span className="font-mono text-2xs text-foreground-secondary">
              {r.strategy_template}
            </span>
          ),
        },
        {
          key: "factors",
          header: "Factors",
          align: "right",
          cell: (r) => Object.keys(r.weights).length,
        },
        {
          key: "sum",
          header: "Σ weight",
          align: "right",
          cell: (r) => (
            <span className="tabular-nums">
              {Object.values(r.weights)
                .reduce((s, v) => s + v, 0)
                .toFixed(3)}
            </span>
          ),
        },
        {
          key: "description",
          header: "Description",
          cell: (r) => (
            <span className="text-xs text-foreground-muted">
              {r.description ?? "—"}
            </span>
          ),
        },
        {
          key: "actions",
          header: "",
          align: "right",
          cell: (r) =>
            r.is_active ? (
              <span className="text-2xs text-foreground-muted">live</span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={activate.isPending}
                onClick={() => activate.mutate(r.id)}
              >
                Activate
              </Button>
            ),
        },
      ]}
    />
  );
}
